import { useState } from "react";
import { Title } from "utils/title";
import { Checkbox, RadioGroup, TextArea } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { fetchApi } from "services/api";
import useConfirm from "components/useConfirm";
import LoadingPanel from "components/LoadingPanel";
import SalespersonAutoComplete from "../Salespeople/SalespersonAutoComplete";
import { SalesPeopleViewModel } from "../Salespeople";

const slotOptions = [
  { label: 'Individual (SP1)', value: 1 },
  { label: 'Account Manager (SP2)', value: 2 },
  { label: 'National (SP3)', value: 3 },
];

const modeOptions = [
  { label: 'By Salesperson', value: 'salesperson' },
  { label: 'By Customer #s', value: 'customers' },
];

const CustomersMassUpdateSalespeople = () => {

  const [slot, setSlot] = useState<number>(1);
  const [mode, setMode] = useState<string>('salesperson');
  const [fromSalesperson, setFromSalesperson] = useState<SalesPeopleViewModel | null>(null);
  const [customerNumbers, setCustomerNumbers] = useState<string>('');
  const [toSalesperson, setToSalesperson] = useState<SalesPeopleViewModel | null>(null);
  const [moveToPrevious, setMoveToPrevious] = useState<boolean>(true);
  const [resetKey, setResetKey] = useState(0);
  const { ConfirmationDialog, confirm } = useConfirm({});
  const [isUpdating, setIsUpdating] = useState(false);

  const parsedCustomerNumbers = customerNumbers.split(/[\s,]+/).map(x => x.trim()).filter(x => x !== '');

  const toSalespersonID = toSalesperson?.SalespersonID ?? 0;
  const toSalespersonName = toSalesperson?.FullName ?? 'no salesperson';

  const canUpdate = (mode === 'salesperson'
    ? fromSalesperson != null && fromSalesperson.SalespersonID !== toSalespersonID
    : parsedCustomerNumbers.length > 0);

  const update = async () => {

    const target = mode === 'salesperson' ? `all customers with ${fromSalesperson.FullName} as SP${slot}` : `${parsedCustomerNumbers.length} customer(s)`;
    const previous = moveToPrevious
      ? `set Previous SP${slot} to ${mode === 'salesperson' ? fromSalesperson.FullName : `their current SP${slot}`}`
      : `set Previous SP${slot} to blank`;
    if (await confirm(`Are you sure you want to set SP${slot} to ${toSalespersonName} and ${previous} for ${target}?`) === false) {
      return;
    }

    setIsUpdating(true);
    const payload = {
      SalespersonSlot: slot,
      FromSalespersonID: mode === 'salesperson' ? fromSalesperson.SalespersonID : null,
      CustomerNumbers: mode === 'customers' ? parsedCustomerNumbers : null,
      ToSalespersonID: toSalespersonID,
      MoveToPrevious: moveToPrevious
    }

    fetchApi('/api/Customer/MassUpdateSalespeople', payload, 'POST')
      .then((updatedCount: number) => {
        setFromSalesperson(null);
        setToSalesperson(null);
        setCustomerNumbers('');
        setResetKey(x => x + 1);

        alert(`${updatedCount} customer(s) updated successfully!`);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to update salespeople');
      })
      .finally(() => setIsUpdating(false));
  }

  return <div className="container mt-2">
    <Title string="Mass Update Salespeople" />
    <ConfirmationDialog />
    {isUpdating && <LoadingPanel />}
    <div className="alert alert-warning" role="alert">
      This updates the salesperson on many customers at once. Changes are recorded in each customer's log.
    </div>

    <div className="form-group row">
      <label className="col-sm-3 col-form-label">Salesperson Slot</label>
      <div className="col-sm-9">
        <RadioGroup layout="horizontal" data={slotOptions} value={slot} onChange={(e) => setSlot(e.value)} />
      </div>
    </div>

    <div className="form-group row">
      <label className="col-sm-3 col-form-label">Find Customers</label>
      <div className="col-sm-9">
        <RadioGroup layout="horizontal" data={modeOptions} value={mode} onChange={(e) => setMode(e.value)} />
      </div>
    </div>

    {mode === 'salesperson' ? <div className="form-group row">
      <label className="col-sm-3 col-form-label">Current Salesperson</label>
      <div className="col-sm-9">
        <SalespersonAutoComplete
          key={`from-${resetKey}`}
          placeholder="type an id or name of the salesperson to replace..."
          includeRecentlyRetiredSalespersons
          onSelectedSalesperson={(e) => setFromSalesperson(e)}
        />
      </div>
    </div> : <div className="form-group row">
      <label className="col-sm-3 col-form-label">Customer #s</label>
      <div className="col-sm-9">
        <TextArea
          rows={5}
          placeholder="Paste comma separated customer numbers..."
          value={customerNumbers}
          onChange={(e) => setCustomerNumbers(e.value)}
        />
        <small className="form-text text-muted">{parsedCustomerNumbers.length} customer number(s).</small>
      </div>
    </div>}

    <div className="form-group row">
      <label className="col-sm-3 col-form-label">New Salesperson</label>
      <div className="col-sm-9">
        <SalespersonAutoComplete
          key={`to-${resetKey}`}
          placeholder="type an id, name or leave blank to remove salesperson"
          onSelectedSalesperson={(e) => setToSalesperson(e)}
        />
      </div>
    </div>

    <Checkbox
      label={`Move existing salesperson to Previous SP (unchecked clears Previous SP)`}
      checked={moveToPrevious}
      onChange={(e) => setMoveToPrevious(e.value)}
    />

    <div className="text-center mt-3">
      <Button
        themeColor="primary"
        disabled={isUpdating || !canUpdate}
        onClick={update}
      >
        Update Salespeople
      </Button>
    </div>
  </div>
}

export default CustomersMassUpdateSalespeople;
